import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import type { ReactNode } from "react"
import {
  GoogleAuthProvider,
  onIdTokenChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User,
} from "firebase/auth"
import { auth } from "./client"

type AuthStatus = "loading" | "signed-in" | "signed-out"

interface FirebaseAuthContextValue {
  user: User | null
  token: string | null
  status: AuthStatus
  error: Error | null
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
}

const FirebaseAuthContext = createContext<FirebaseAuthContextValue | null>(null)

export const FirebaseAuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [status, setStatus] = useState<AuthStatus>("loading")
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (nextUser) => {
      if (nextUser) {
        setStatus("signed-in")
        setUser(nextUser)
        setToken(await nextUser.getIdToken())
        setError(null)
      } else {
        setStatus("signed-out")
        setUser(null)
        setToken(null)
        setError(null)
      }
    })
    return unsubscribe
  }, [])

  const signInWithGoogle = useCallback(async () => {
    setStatus("loading")
    setError(null)

    try {
      const provider = new GoogleAuthProvider()
      provider.setCustomParameters({ prompt: "select_account" })
      await signInWithPopup(auth, provider)
      setStatus("signed-in")
    } catch (err) {
      setStatus("signed-out")
      setError(err as Error)
      throw err
    }
  }, [])

  const signOut = useCallback(async () => {
    setStatus("loading")
    setError(null)
    try {
      await firebaseSignOut(auth)
      setStatus("signed-out")
    } catch (err) {
      setStatus("signed-in")
      setError(err as Error)
      throw err
    }
  }, [])

  const value = useMemo(
    () => ({
      user,
      token,
      status,
      error,
      signInWithGoogle,
      signOut,
    }),
    [user, token, status, error, signInWithGoogle, signOut],
  )

  return <FirebaseAuthContext.Provider value={value}>{children}</FirebaseAuthContext.Provider>
}

export const useFirebaseAuth = () => {
  const context = useContext(FirebaseAuthContext)
  if (!context) {
    throw new Error("useFirebaseAuth must be wrapped with FirebaseAuthProvider")
  }
  return context
}
